import axios from 'axios';
import type { CancelTokenSource } from 'axios';

import type { AttributionHistoryEntry } from 'stores/zustand/attributionStore';

import type { ServerError, TaxDocumentVo, UserVo } from '../../models';
import type {
    GetProfileResponse,
    GetUserBlockInfoResponse,
    GetUserTaxDocumentsResponse,
    UpdateUserEmailRequest,
    UpdateUserEmailWithCodeRequest,
    UpdateUserRequest,
    UpdateUserSsn,
    VerifyAccountRequest,
    VerifyPendingEmailRequest,
} from '../../models/reqres';
import { exchangeApi, handleErrorCallback, noop, rateLimiter } from '../core';

const USEFUL_UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'channel'];
const CLICK_ID_KEYS = ['gclid', 'fbclid', 'msclkid', 'dclid', 'rdt_cid', 'ttclid', 'twclid', 'sc_cid'];
const OPTIONAL_UTM_KEYS = ['utm_landing', 'event_id'];

export function getProfile(
    userId: string,
    onSuc: (user: UserVo) => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetProfileResponse>(`/users/${userId}`, {
            cancelToken: cancelRef.token,
            params: { allowRequestEvenIfPageIsHidden: true },
        })
        .then((response) => {
            onSuc(response.data.user);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(onFinally);

    return cancelRef;
}

export function updateUserInfo(
    userId: string,
    payload: UpdateUserRequest,
    onSuc: () => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();
    exchangeApi
        .put(`/users/${userId}`, payload, { cancelToken: cancelRef.token })
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function addUserAttributionTouchpoint(
    userId: string,
    touchpoint: AttributionHistoryEntry,
    onSuc: () => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    // Map the touchpoint fields to the expected API field names
    const mappedTouchpoint: any = {};

    // Ensure timestamp is a valid ISO string - REQUIRED field
    if (touchpoint.timestamp) {
        const date = new Date(touchpoint.timestamp);
        if (!isNaN(date.getTime())) {
            mappedTouchpoint.timestamp = date.toISOString();
        }
    } else {
        // Use current timestamp if not provided
        mappedTouchpoint.timestamp = new Date().toISOString();
    }

    // Map utm_landing to landing_page
    if (touchpoint.utm_landing) {
        mappedTouchpoint.landing_page = touchpoint.utm_landing;
    }

    // Only send if there's at least one meaningful field present (UTM/channel or click ID).
    // A touchpoint with only utm_landing (or nothing at all) is not worth sending.
    const hasUsefulUtm = USEFUL_UTM_KEYS.some((k) => !!(touchpoint as any)[k]);
    const hasClickId = CLICK_ID_KEYS.some((k) => !!(touchpoint as any)[k]);
    if (!hasUsefulUtm && !hasClickId) {
        // Nothing meaningful to send — return early.
        onFinally();
        return cancelRef;
    }
    if (touchpoint.utm_landing) {
        mappedTouchpoint.landing_page = touchpoint.utm_landing;
    }

    // Map UTM parameters to the expected field names
    if (touchpoint.utm_source) mappedTouchpoint.source = touchpoint.utm_source;
    if (touchpoint.utm_medium) mappedTouchpoint.medium = touchpoint.utm_medium;
    if (touchpoint.utm_campaign) mappedTouchpoint.campaign = touchpoint.utm_campaign;
    if (touchpoint.utm_content) mappedTouchpoint.content = touchpoint.utm_content;
    if (touchpoint.utm_term) mappedTouchpoint.term = touchpoint.utm_term;

    // Include other fields if they exist
    if (touchpoint.channel) mappedTouchpoint.channel = touchpoint.channel;
    if (touchpoint.event_id) mappedTouchpoint.event_id = touchpoint.event_id;

    // Include click IDs, cid, anonymous_id, and initial_referrer
    if (touchpoint.gclid != null) mappedTouchpoint.gclid = touchpoint.gclid;
    if (touchpoint.fbclid != null) mappedTouchpoint.fbclid = touchpoint.fbclid;
    if (touchpoint.msclkid != null) mappedTouchpoint.msclkid = touchpoint.msclkid;
    if (touchpoint.dclid != null) mappedTouchpoint.dclid = touchpoint.dclid;
    if (touchpoint.rdt_cid != null) mappedTouchpoint.rdt_cid = touchpoint.rdt_cid;
    if (touchpoint.ttclid != null) mappedTouchpoint.ttclid = touchpoint.ttclid;
    if (touchpoint.twclid != null) mappedTouchpoint.twclid = touchpoint.twclid;
    if (touchpoint.sc_cid != null) mappedTouchpoint.sc_cid = touchpoint.sc_cid;
    if (touchpoint.cid != null) mappedTouchpoint.cid = touchpoint.cid;
    if (touchpoint.anonymous_id != null) mappedTouchpoint.anonymous_id = touchpoint.anonymous_id;
    if (touchpoint.initial_referrer != null) mappedTouchpoint.initial_referrer = touchpoint.initial_referrer;

    exchangeApi
        .post(`/users/${userId}/attribution/touchpoints`, mappedTouchpoint, { cancelToken: cancelRef.token })
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function setUserAttribution(
    userId: string,
    attributionHistory: AttributionHistoryEntry[],
    onSuc: () => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    // Map the client-side field names to the expected API field names
    const mappedTouchpoints = attributionHistory
        .map((touchpoint) => {
            const mapped: any = {};

            // Ensure timestamp is a valid ISO string - REQUIRED field
            if (touchpoint.timestamp) {
                // Convert to Date if it's not already, then to ISO string
                const date = new Date(touchpoint.timestamp);
                if (!isNaN(date.getTime())) {
                    mapped.timestamp = date.toISOString();
                }
            }

            // Skip touchpoints without valid timestamps
            if (!mapped.timestamp) {
                console.warn('Skipping touchpoint without valid timestamp:', touchpoint);
                return null;
            }

            // Map utm_landing to landing_page
            if (touchpoint.utm_landing) {
                mapped.landing_page = touchpoint.utm_landing;
            }

            // Map UTM parameters to the expected field names
            // Client sends utm_source, API expects source
            if (touchpoint.utm_source) mapped.source = touchpoint.utm_source;
            if (touchpoint.utm_medium) mapped.medium = touchpoint.utm_medium;
            if (touchpoint.utm_campaign) mapped.campaign = touchpoint.utm_campaign;
            if (touchpoint.utm_content) mapped.content = touchpoint.utm_content;
            if (touchpoint.utm_term) mapped.term = touchpoint.utm_term;
            if (touchpoint.channel) mapped.channel = touchpoint.channel;
            if (touchpoint.event_id) mapped.event_id = touchpoint.event_id;

            // Include click IDs, cid, anonymous_id, and initial_referrer
            if (touchpoint.gclid != null) mapped.gclid = touchpoint.gclid;
            if (touchpoint.fbclid != null) mapped.fbclid = touchpoint.fbclid;
            if (touchpoint.msclkid != null) mapped.msclkid = touchpoint.msclkid;
            if (touchpoint.dclid != null) mapped.dclid = touchpoint.dclid;
            if (touchpoint.rdt_cid != null) mapped.rdt_cid = touchpoint.rdt_cid;
            if (touchpoint.ttclid != null) mapped.ttclid = touchpoint.ttclid;
            if (touchpoint.twclid != null) mapped.twclid = touchpoint.twclid;
            if (touchpoint.sc_cid != null) mapped.sc_cid = touchpoint.sc_cid;
            if (touchpoint.cid != null) mapped.cid = touchpoint.cid;
            if (touchpoint.anonymous_id != null) mapped.anonymous_id = touchpoint.anonymous_id;
            if (touchpoint.initial_referrer != null) mapped.initial_referrer = touchpoint.initial_referrer;
            return mapped;
        })
        // Filter out touchpoints that have nothing other than landing_page and/or event_id
        .filter((tp) => {
            if (!tp) return false;
            // If there is any key other than landing_page or event_id, keep it
            return Object.keys(tp).some((key) => !OPTIONAL_UTM_KEYS.includes(key));
        });

    // Wrap the attribution history in the expected request structure
    // Include empty UTM fields since the database has NOT NULL constraints with defaults
    const requestPayload: any = {
        touchpoints: mappedTouchpoints,
        utm_source: '',
        utm_medium: '',
        utm_campaign: '',
        utm_term: '',
        utm_content: '',
    };

    exchangeApi
        .put(`/users/${userId}/attribution`, requestPayload, { cancelToken: cancelRef.token })
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function requestUpdateUserEmail(
    userId: string,
    payload: UpdateUserEmailRequest,
    onSuc: () => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();
    exchangeApi
        .post(`/users/${userId}/email/request-update?four_digit_code=true`, payload, {
            cancelToken: cancelRef.token,
        })
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function updateUserEmail(
    userId: string,
    payload: UpdateUserEmailWithCodeRequest,
    onSuc: () => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();
    exchangeApi
        .put(`/users/${userId}/email?four_digit_code=true`, payload, { cancelToken: cancelRef.token })
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function verifyPendingEmail(
    userId: string,
    payload: VerifyPendingEmailRequest,
    onSuc: () => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();
    exchangeApi
        .post(`/users/${userId}/verify_pending_email`, payload, { cancelToken: cancelRef.token })
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function updateUserSsn(
    userId: string,
    payload: UpdateUserSsn,
    onSuc: () => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();
    exchangeApi
        .put(`/users/${userId}/ssn`, payload, { cancelToken: cancelRef.token })
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getBlockInfo(
    userId: string,
    onSuc: (response: GetUserBlockInfoResponse) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetUserBlockInfoResponse>(`/users/${userId}/blocks`, { cancelToken: cancelRef.token })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function verifyAccount(
    userId: string,
    payload: VerifyAccountRequest,
    onSuc: () => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post(`/users/${userId}/verify`, payload, { cancelToken: cancelRef.token })
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function resendAccountVerification(
    userId: string,
    onSuc: () => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post(`/users/${userId}/email_confirmation?four_digit_code=true`, {}, { cancelToken: cancelRef.token })
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getUserTaxDocument(
    userId: string,
    year: string,
    type: string,
    onSuc: (documents: TaxDocumentVo[]) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    rateLimiter
        .getLimiter('getUserTaxDocument')
        .schedule(() =>
            exchangeApi.get<GetUserTaxDocumentsResponse>(`/users/${userId}/tax-documents/${year}/version/${type}`, {
                cancelToken: cancelRef.token,
            }),
        )
        .then((response) => {
            onSuc(response.data.documents);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}
